import React from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../../hooks/useProducts';
import { extractPlainText } from '../../utils/richTextRenderer';
import { getImageUrl } from '../../services/api';

const AllProductsGrid = () => {
  const { products, loading, error } = useProducts();

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-catalina-blue"></div>
            <p className="mt-4 text-gray-600">Loading all products...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-red-600">
            <p>Error loading products: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!products || products.length === 0) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-600">
            <p>No products available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-catalina-blue mb-4">All Products</h2>
          <p className="text-lg text-gray-600">
            Browse our complete range of industrial equipment solutions
          </p>
          <div className="mt-4 text-sm text-gray-500">
            Showing {products.length} {products.length === 1 ? 'product' : 'products'}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => {
            const imageUrl = product.Image ? getImageUrl(product.Image, 'medium') : null;
            
            return (
              <div
                key={product.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition duration-300"
              >
                {/* Product Image */}
                {imageUrl ? (
                  <div className="w-full h-48 bg-white overflow-hidden">
                    <img 
                      src={imageUrl} 
                      alt={product.Image?.alternativeText || product.Title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                
                {/* Product Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{product.Title}</h3>
                  
                  {/* Category Badge */}
                  {product.product_category && (
                    <div className="mb-3">
                      <span className="inline-block bg-catalina-blue/10 text-catalina-blue text-xs font-semibold px-3 py-1 rounded-full">
                        {product.product_category.Name || 'Uncategorized'}
                      </span>
                    </div>
                  )}
                  
                  {product.Description && (
                    <p className="text-gray-600 mb-6 leading-relaxed line-clamp-3">
                      {extractPlainText(product.Description, 100)}
                    </p>
                  )}
                  
                  <Link
                    to={`/product/${product.slug}`}
                    className="inline-flex items-center text-catalina-blue font-semibold hover:text-san-marino transition duration-300"
                  >
                    View Details
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AllProductsGrid;